import React, { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { NotificationContext, NotificationContextValue } from './NotificationContextDef';
import { notificationService } from '../services/notificationService';
import { useAuth } from '../hooks/useAuth';
import { Notification } from '../types/workflow';

/** Polling interval for unread count (30 seconds) */
const POLLING_INTERVAL_MS = 30_000;

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastMarkAllReadAt, setLastMarkAllReadAt] = useState<number | null>(null);
  const { isAuthenticated, isExternal, isPartner } = useAuth();
  // Notifications are an internal-staff feature; the External/Partner boundary filter 403s the
  // endpoints, so polling for portal-confined roles is pure noise (a silent 403 every 30s).
  const canUseNotifications = isAuthenticated && !isExternal && !isPartner;

  const refreshCount = useCallback(async () => {
    try {
      const count = await notificationService.getCount();
      setUnreadCount(count.unreadCount);
    } catch {
      // Silent -- polling failure should not disrupt UI
    }
  }, []);

  const refreshNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await notificationService.getNotifications({
        pageSize: 10,
        pageNumber: 1,
      });
      setRecentNotifications(response.items);
    } catch {
      // Silent -- dropdown fetch failure handled by empty state
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Poll unread count when authenticated (internal roles only)
  useEffect(() => {
    if (!canUseNotifications) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- refreshCount is async; state is set after an awaited API poll, not synchronously in the effect body
    refreshCount();
    const interval = setInterval(refreshCount, POLLING_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [canUseNotifications, refreshCount]);

  // Reset all state on logout to prevent stale data bleeding into the next session.
  // The NotificationProvider wraps the router and is NOT unmounted on logout.
  useEffect(() => {
    if (!isAuthenticated) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional logout reset: provider wraps the router and never unmounts, so state must be cleared when auth (external system) changes
      setUnreadCount(0);
      setRecentNotifications([]);
      setLastMarkAllReadAt(null);
    }
  }, [isAuthenticated]);

  const markAsRead = useCallback(async (id: number) => {
    await notificationService.markAsRead(id);
    setRecentNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, isRead: true } : n))
    );
    // Optimistic decrement
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(async () => {
    await notificationService.markAllAsRead();
    setUnreadCount(0);
    setRecentNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setLastMarkAllReadAt(Date.now());
  }, []);

  const deleteNotification = useCallback(async (id: number) => {
    const notification = recentNotifications.find(n => n.id === id);
    await notificationService.delete(id);
    setRecentNotifications(prev => prev.filter(n => n.id !== id));
    if (notification && !notification.isRead) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  }, [recentNotifications]);

  const value = useMemo<NotificationContextValue>(() => ({
    unreadCount,
    recentNotifications,
    isLoading,
    lastMarkAllReadAt,
    refreshCount,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  }), [
    unreadCount,
    recentNotifications,
    isLoading,
    lastMarkAllReadAt,
    refreshCount,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
